import React, { useMemo, useState } from 'react';
import type { StatusLight } from '@features/patient-coverages';
import { API_STATUS_TO_LIGHT, BillingTab, getFinancials, STATUS_CONFIG } from '@features/patient-coverages';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { AppointmentResponse } from '@store/generated/appointments';
import type { EligibilityCheckResponse } from '@store/generated/eligibility';
import { format, formatDistanceToNow, parseISO } from 'date-fns';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StatusFilter = 'all' | StatusLight | 'NOT_CHECKED';

type SortField = 'patient' | 'appointment';
type SortDirection = 'asc' | 'desc';

export interface BatchEligibilityTableProps {
  appointments: AppointmentResponse[];
  eligibilityByPatient: Record<string, EligibilityCheckResponse>;
  loading?: boolean;
}

// ---------------------------------------------------------------------------
// Filter chip definitions
// ---------------------------------------------------------------------------

const FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'GREEN', label: 'Verified' },
  { value: 'YELLOW', label: 'Needs Review' },
  { value: 'RED', label: 'Issue Detected' },
  { value: 'NOT_CHECKED', label: 'Not Checked' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getPatientName(appointment: AppointmentResponse): string {
  const patient = appointment.patient;
  if (!patient) return 'Unknown';
  if (patient.first_name || patient.last_name) {
    return [patient.first_name, patient.last_name].filter(Boolean).join(' ');
  }
  return patient.name ?? 'Unknown';
}

function formatAppointmentDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, h:mm a');
  } catch {
    return iso;
  }
}

function formatLastChecked(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

function formatDollars(dollars: number | null | undefined): string {
  if (dollars == null) return '—';
  return `$${dollars.toFixed(2)}`;
}

function getStatusLight(
  eligibility: EligibilityCheckResponse | undefined,
): StatusLight | null {
  if (!eligibility) return null;
  return API_STATUS_TO_LIGHT[eligibility.status] ?? null;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatusChip: React.FC<{ statusLight: StatusLight | null }> = ({
  statusLight,
}) => {
  const theme = useTheme();

  if (!statusLight) {
    return (
      <Chip
        icon={<HelpOutlineIcon />}
        label="Not Checked"
        size="small"
        variant="outlined"
        sx={{ color: theme.palette.text.secondary, borderColor: theme.palette.divider }}
      />
    );
  }

  const config = STATUS_CONFIG[statusLight];
  const IconComponent = config.icon;

  return (
    <Chip
      icon={<IconComponent />}
      label={config.label}
      size="small"
      color={config.palette}
      variant="filled"
    />
  );
};

const SkeletonRows: React.FC = () => (
  <>
    {Array.from({ length: 5 }).map((_, i) => (
      <TableRow key={i}>
        <TableCell>
          <Skeleton width={140} />
        </TableCell>
        <TableCell>
          <Skeleton width={120} />
        </TableCell>
        <TableCell>
          <Skeleton width={100} />
        </TableCell>
        <TableCell>
          <Skeleton width={110} />
        </TableCell>
        <TableCell>
          <Skeleton width={80} />
        </TableCell>
        <TableCell>
          <Skeleton width={90} />
        </TableCell>
      </TableRow>
    ))}
  </>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const BatchEligibilityTable: React.FC<BatchEligibilityTableProps> = ({
  appointments,
  eligibilityByPatient,
  loading = false,
}) => {
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<SortField>('appointment');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);

  const handleSort = (field: SortField): void => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSorted = useMemo(() => {
    // Filter
    const filtered = appointments.filter((appt) => {
      if (filter === 'all') return true;

      const patientId = appt.patient?.patient_id;
      const eligibility = patientId
        ? eligibilityByPatient[patientId]
        : undefined;
      const light = getStatusLight(eligibility);

      if (filter === 'NOT_CHECKED') return light === null;
      return light === filter;
    });

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      const multiplier = sortDirection === 'asc' ? 1 : -1;

      if (sortField === 'patient') {
        return multiplier * getPatientName(a).localeCompare(getPatientName(b));
      }

      // appointment (by start_time)
      return (
        multiplier *
        (new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
      );
    });

    return sorted;
  }, [appointments, eligibilityByPatient, filter, sortField, sortDirection]);

  // ---- Empty state ----
  if (!loading && appointments.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 8,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No appointments found in the next 2 weeks.
        </Typography>
      </Box>
    );
  }

  const ariaSort = (
    field: SortField,
  ): 'ascending' | 'descending' | undefined => {
    if (sortField !== field) return undefined;
    return sortDirection === 'asc' ? 'ascending' : 'descending';
  };

  return (
    <Box>
      {/* Filter chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {FILTER_OPTIONS.map((option) => (
          <Chip
            key={option.value}
            label={option.label}
            variant={filter === option.value ? 'filled' : 'outlined'}
            color={filter === option.value ? 'primary' : 'default'}
            onClick={() => setFilter(option.value)}
            aria-pressed={filter === option.value}
            size="small"
          />
        ))}
      </Stack>

      {/* Table */}
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table aria-label="Batch eligibility results" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell aria-sort={ariaSort('patient')}>
                <TableSortLabel
                  active={sortField === 'patient'}
                  direction={sortField === 'patient' ? sortDirection : 'asc'}
                  onClick={() => handleSort('patient')}
                >
                  Patient
                </TableSortLabel>
              </TableCell>
              <TableCell aria-sort={ariaSort('appointment')}>
                <TableSortLabel
                  active={sortField === 'appointment'}
                  direction={
                    sortField === 'appointment' ? sortDirection : 'asc'
                  }
                  onClick={() => handleSort('appointment')}
                >
                  Next Appointment
                </TableSortLabel>
              </TableCell>
              <TableCell>Encounter Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Est. Responsibility</TableCell>
              <TableCell>Last Checked</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <SkeletonRows />
            ) : (
              filteredAndSorted.map((appt) => {
                const patientId = appt.patient?.patient_id;
                const eligibility = patientId
                  ? eligibilityByPatient[patientId]
                  : undefined;
                const light = getStatusLight(eligibility);

                return (
                  <TableRow
                    key={appt.appointment_id}
                    hover
                    selected={patientId === selectedPatientId}
                    onClick={() =>
                      setSelectedPatientId((prev) =>
                        prev === patientId ? null : (patientId ?? null),
                      )
                    }
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {getPatientName(appt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatAppointmentDate(appt.start_time)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {appt.encounter_type ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip statusLight={light} />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const obligation = getFinancials(eligibility).member_obligation;
                        return (
                          <Typography
                            variant="body2"
                            fontWeight={obligation != null ? 600 : 400}
                            color={obligation != null ? 'text.primary' : 'text.secondary'}
                          >
                            {formatDollars(obligation)}
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatLastChecked(eligibility?.last_checked_at)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail panel */}
      {selectedPatientId && (
        <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {getPatientName(
                appointments.find((a) => a.patient?.patient_id === selectedPatientId) ??
                  ({} as AppointmentResponse),
              )}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setSelectedPatientId(null)}
              aria-label="Close detail panel"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <BillingTab patientId={selectedPatientId} />
        </Paper>
      )}

      {/* No results after filtering */}
      {!loading && filteredAndSorted.length === 0 && appointments.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No appointments match the selected filter.
          </Typography>
        </Box>
      )}
    </Box>
  );
};
